import {
  Box,
  Stack,
  Paper,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";

import "dayjs/locale/pt-br";
import { Dispatch, SetStateAction, useState, useMemo } from "react";
import { NoDataWarning } from "../common/NoDataWarning";
import { ScheduleQueue } from "./ScheduleQueue";
import { CircularLoading } from "../common/CircularLoading";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";

import { DateFilterType } from "@/utils/interfaces";

interface IProps {
  bundle: any | undefined;

  setRefetch?: Dispatch<SetStateAction<boolean>> | (() => void);

  loading: boolean;
  filterType?: DateFilterType;
}

export function AppointmentsViewer({ bundle, loading, setRefetch, filterType }: IProps) {
  const { contractHealthPlan } = useHealthPlanContractContext();
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Filtro para consultas particulares (sem plano de saúde)
  const filteredPrivate = useMemo(() => {
    if (!bundle?.entry) return bundle;

    const filteredEntries = bundle.entry.filter((entry: any) => {
      if (entry.resource?.resourceType === "Appointment") {
        const hasInsurance = entry.resource?.supportingInformation;
        return !hasInsurance; // Consultas particulares não têm supportingInformation
      }
      return true;
    });

    return {
      ...bundle,
      entry: filteredEntries,
    };
  }, [bundle]);

  // Filtro para consultas com plano de saúde
  const filteredHealthPlan = useMemo(() => {
    if (!bundle?.entry) return bundle;

    const filteredEntries = bundle.entry.filter((entry: any) => {
      if (entry.resource?.resourceType === "Appointment") {
        const hasInsurance = entry.resource?.supportingInformation;
        return hasInsurance; // Consultas com plano têm supportingInformation
      }
      return true;
    });

    return {
      ...bundle,
      entry: filteredEntries,
    };
  }, [bundle]);



  // Bundle filtrado baseado na tab selecionada
  const filteredBundle = selectedTab === 0 ? filteredPrivate : filteredHealthPlan;

  const hasAppointments = filteredBundle?.entry?.some(
    (entry: any) => entry.resource?.resourceType === "Appointment"
  );

  return (
    <>
      {/* Appointments list */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          height: "fit-content",
          overflow: "hidden",
        }}
      >
        {contractHealthPlan && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{ minHeight: 48 }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Particulares</span>
                    {(() => {
                      const count = bundle?.entry?.filter((entry: any) => {
                        if (entry.resource?.resourceType === "Appointment") {
                          const hasInsurance = entry.resource?.supportingInformation;
                          return !hasInsurance;
                        }
                        return false;
                      }).length || 0;
                      
                      return count >= 0 ? (
                        <Chip
                          label={count}
                          size="small"
                          color="primary"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        />
                      ) : null;
                    })()}
                  </Box>
                }
                sx={{
                  textTransform: "none",
                }}
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Plano de Saúde</span>
                    {(() => {
                      const count = bundle?.entry?.filter((entry: any) => {
                        if (entry.resource?.resourceType === "Appointment") {
                          const hasInsurance = entry.resource?.supportingInformation;
                          return hasInsurance;
                        }
                        return false;
                      }).length || 0;
                      
                      return count >= 0 ? (
                        <Chip
                          label={count}
                          size="small"
                          color="primary"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        />
                      ) : null;
                    })()}
                  </Box>
                }
                sx={{
                  textTransform: "none",
                }}
              />
            </Tabs>
          </Box>
        )}
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "0.6em",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary700.main",
            },
          }}
        >
          {loading ? (
            <Stack
              height="100%"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              p={3}
            >
              <CircularLoading />
            </Stack>
          ) : hasAppointments ? (
            <ScheduleQueue
              setRefetch={setRefetch}
              bundle={filteredBundle}
              loading={loading}
              filterType={filterType}
            />
          ) : (
            <Stack
              height="100%"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              p={3}
            >
              <NoDataWarning message="Não há consultas agendadas para exibir" />
            </Stack>
          )}
        </Box>
      </Paper>
    </>
  );
}
